import { DEFAULT_ROUTINE } from "@/data/defaultRoutine";
import { DATA_KEYS, readJSON, writeJSON, removeKey } from "@/storage/local";

// Una cuenta nueva empieza sin días: la rutina la monta cada uno. DEFAULT_ROUTINE
// sigue existiendo para "Reiniciar progreso", que restaura el PPL de fábrica.
const BLANK_STATE = {
  routine: [],
  progress: {},
  alternatives: {},
  userName: "",
  // Ciclo de entrenamiento en curso, no semana del calendario.
  week: 1,
  weekStartedAt: null,
};

// Build name→img lookup from the current default routine
const DEFAULT_IMG_MAP = {};
for (const day of DEFAULT_ROUTINE) {
  for (const ex of day.exercises) {
    if (ex.img) DEFAULT_IMG_MAP[ex.name] = ex.img;
  }
}

function mergeDefaultImages(routine) {
  return routine.map((day) => ({
    ...day,
    exercises: day.exercises.map((ex) => {
      // Keep user-uploaded images (http URLs or non-SVG data URIs)
      const hasCustomImg = ex.img && !ex.img.startsWith("data:image/svg+xml,");
      if (hasCustomImg) return ex;
      const defaultImg = DEFAULT_IMG_MAP[ex.name];
      return defaultImg ? { ...ex, img: defaultImg } : ex;
    }),
  }));
}

export function loadState() {
  const parsed = readJSON(DATA_KEYS.APP, null);
  // Solo se llega aquí sin datos guardados, es decir, en una cuenta recién
  // creada: una cuenta con rutina propia nunca pasa por esta rama.
  if (!parsed || !Array.isArray(parsed.routine)) {
    return { ...BLANK_STATE };
  }
  return {
    routine: mergeDefaultImages(parsed.routine),
    progress: parsed.progress || {},
    alternatives: parsed.alternatives || {},
    userName: parsed.userName || "",
    week: parsed.week || 1,
    weekStartedAt: parsed.weekStartedAt || null,
  };
}

export function saveState(state) {
  writeJSON(DATA_KEYS.APP, state);
}

export function resetState(currentName) {
  const next = {
    routine: DEFAULT_ROUTINE,
    progress: {},
    alternatives: {},
    userName: currentName,
    // Reiniciar el progreso no borra el historial de ciclos, así que el
    // contador sigue donde estaba para no repetir números de semana.
    week: loadState().week || 1,
    weekStartedAt: null,
  };
  saveState(next);
  return next;
}

export function loadHistory() {
  return readJSON(DATA_KEYS.HISTORY, {});
}

export function appendHistory(exerciseName, weight) {
  if (!weight || weight <= 0) return;
  const history = loadHistory();
  const entries = history[exerciseName] || [];
  entries.push({ date: new Date().toISOString(), weight });
  history[exerciseName] = entries.slice(-50);
  writeJSON(DATA_KEYS.HISTORY, history);
}

// Updates today's history entry if it exists, otherwise appends a new one
export function upsertTodayHistory(exerciseName, weight) {
  if (!weight || weight <= 0) return;
  const today = new Date().toISOString().split("T")[0];
  const history = loadHistory();
  const entries = history[exerciseName] || [];
  const todayIdx = entries.findIndex((e) => e.date.startsWith(today));
  if (todayIdx >= 0) {
    entries[todayIdx] = { ...entries[todayIdx], weight };
  } else {
    entries.push({ date: new Date().toISOString(), weight });
  }
  history[exerciseName] = entries.slice(-50);
  writeJSON(DATA_KEYS.HISTORY, history);
}

export function clearHistory() {
  removeKey(DATA_KEYS.HISTORY);
}

export function loadTrainingDates() {
  return readJSON(DATA_KEYS.TRAINING_DATES, []);
}

export function recordTrainingDate() {
  const today = new Date().toISOString().split("T")[0];
  const dates = loadTrainingDates();
  if (!dates.includes(today)) {
    dates.push(today);
    writeJSON(DATA_KEYS.TRAINING_DATES, dates.sort().slice(-365));
  }
}

export function clearTrainingDates() {
  removeKey(DATA_KEYS.TRAINING_DATES);
}

// Días seguidos entrenando hasta hoy. Entrenar hoy todavía no es obligatorio
// para tener racha: si aún no se ha entrenado, se cuenta desde ayer, porque el
// día no ha terminado.
export function computeStreak(dates) {
  if (!dates || dates.length === 0) return 0;
  const dateSet = new Set(dates);
  let streak = 0;
  const cursor = new Date();
  const todayKey = cursor.toISOString().split("T")[0];
  if (!dateSet.has(todayKey)) {
    cursor.setDate(cursor.getDate() - 1);
  }
  while (streak < 365) {
    const key = cursor.toISOString().split("T")[0];
    if (dateSet.has(key)) {
      streak++;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }
  return streak;
}

// La racha más larga registrada, que puede ser anterior a la actual.
export function longestStreak(dates) {
  if (!dates || dates.length === 0) return 0;
  const sorted = [...new Set(dates)].sort();
  let best = 1;
  let run = 1;
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(`${sorted[i - 1]}T00:00:00Z`);
    const curr = new Date(`${sorted[i]}T00:00:00Z`);
    const gapDays = Math.round((curr - prev) / 86400000);
    run = gapDays === 1 ? run + 1 : 1;
    if (run > best) best = run;
  }
  return best;
}

export function countSets(routine, progress) {
  let total = 0;
  let done = 0;
  routine.forEach((day, dIdx) => {
    day.exercises.forEach((ex) => {
      for (let s = 0; s < ex.sets; s++) {
        total += 1;
        const key = `${dIdx}-${ex.id}-${s}`;
        if (progress[key]?.done) done += 1;
      }
    });
  });
  return { total, done };
}

export function countSetsForDay(routine, progress, dayIdx) {
  const day = routine[dayIdx];
  if (!day) return { total: 0, done: 0 };
  let total = 0;
  let done = 0;
  day.exercises.forEach((ex) => {
    for (let s = 0; s < ex.sets; s++) {
      total += 1;
      const key = `${dayIdx}-${ex.id}-${s}`;
      if (progress[key]?.done) done += 1;
    }
  });
  return { total, done };
}
